import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { isAdaAssetId } from "@sundae/cardano-utils";
import { useI18N } from "@sundae/react-hooks";
import {
  Avatar,
  AvatarGroup,
  Balance,
  Button,
  Percentage,
  Table,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import {
  ColumnDef,
  SortingState,
  TableOptions,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import cx from "classnames";
import { FC, useCallback, useMemo, useState } from "react";

import { uniqBy } from "lodash";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import { testIds } from "../../../constants/testIds.constants";
import { useYieldFarmingPositions } from "../../../hooks/farms/useYieldFarmingPositions";
import PendingTransactions from "../../../i18n/components/farms/PendingTransactions/PendingTransactions.trans";
import { useProfileAppContext } from "../../../stores";
import { ExchangeContextProvider } from "../../../stores/ExchangeContext";
import { IEnhancedValue } from "../../../types/Farm.types";
import { getAssetLogo, getAssetName } from "../../../utils/assets.utils";
import { getPoolName, isLPAsset } from "../../../utils/pool.utils";
import AssetWithAssetName from "../../AssetWithAssetName";
import UnlockYieldFarmingPositionsDialog from "../../Dialogs/UnlockYieldFarmingPositionsDialog/UnlockYieldFarmingPositionsDialog";
import UpdateYieldFarmingPositionDialog from "../../Dialogs/UpdateYieldFarmingPositionDialog";
import NoDataIndicator from "../../NoDataIndicator";
import PoolHoverCard from "../../PoolHoverCard";
import { YieldFarmingPositionDialogContextProvider } from "./context";

interface IPositionTableProps {
  description?: string;
  hideButton?: boolean;
  title: string;
}

const PositionTable: FC<IPositionTableProps> = ({ hideButton, title, description }) => {
  const { details } = useWalletDetails();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    isLoading: isLoadingYieldFarmingPositions,
    mergedPosition,
    lpAssetsPoolMap,
    pendingPositionTransactions,
    pendingDelegationTransactions,
    pendingYieldFarmingTransactions,
    pendingUnlockingPositionTransactions,
    setPendingYieldFarmingTransactions,
  } = useYieldFarmingPositions({
    beneficiary: [details.stakingAddress, details.mainAddress].sort(),
  });
  const { currentLocale, t } = useI18N("farms");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUnlockDialogOpen, setIsUnlockDialogOpen] = useState(false);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleOpenUnlockDialog = () => setIsUnlockDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleCloseUnlockDialog = () => setIsUnlockDialogOpen(false);
  const isLoading = useMemo(
    () =>
      isLoadingYieldFarmingPositions ||
      Boolean(pendingPositionTransactions.length) ||
      Boolean(pendingUnlockingPositionTransactions.length) ||
      Boolean(pendingDelegationTransactions.length),
    [
      isLoadingYieldFarmingPositions,
      pendingPositionTransactions.length,
      pendingUnlockingPositionTransactions.length,
      pendingDelegationTransactions.length,
    ]
  );
  const handleOnMutationSuccess = useCallback(
    (txHash: string) => {
      if (txHash) {
        setPendingYieldFarmingTransactions(
          uniqBy(
            [
              ...pendingYieldFarmingTransactions,
              {
                address: details?.stakingAddress,
                expires: Date.now() + 1000 * 60 * 10, // 10 minutes
                type: "position",
                txHash,
              },
            ],
            "txHash"
          )
        );
      }
    },
    [pendingYieldFarmingTransactions, details?.stakingAddress]
  );

  const columns = useMemo<ColumnDef<IEnhancedValue>[]>(
    () => [
      {
        header: t("portfolio.positions.tableHeader.token"),
        accessorKey: "asset",
        id: "asset",
        cell: ({ row: { original } }) => {
          if (original.pool) {
            return (
              <PoolHoverCard pool={original.pool}>
                <AvatarGroup>
                  <Avatar
                    size="xs"
                    src={getAssetLogo(original.pool.assetA) || ""}
                    stacked
                    stackedDiagonal={false}
                  />
                  <Avatar
                    size="xs"
                    src={getAssetLogo(original.pool.assetB) || ""}
                    stacked
                    stackedDiagonal={false}
                  />
                </AvatarGroup>
                <Text tag="span" size="xs">
                  {getPoolName({ assetA: original.pool.assetA, assetB: original.pool.assetB })} LP
                </Text>
              </PoolHoverCard>
            );
          }

          return (
            <AssetWithAssetName
              assets={original.asset.metadata}
              assetName={getAssetName(original.asset.metadata)}
              assetNameSize="xs"
              size="xs"
            />
          );
        },
      },
      {
        header: t("portfolio.positions.tableHeader.amount"),
        accessorKey: "lockedAmount",
        cell: ({ row: { original } }) => {
          const isLP = isLPAsset(original.asset.metadata.assetId);
          const assetDecimals = isLP
            ? // since we're rendering LPs converted to their ADA value, we need to use ADA's decimals
              ADA_METADATA.decimals
            : original.asset.metadata.decimals;
          const maximumFractionDigits = Number.isInteger(
            original.totalAmountInADA?.value.toNumber() ?? 0
          )
            ? // If the amount is a whole number (i.e. 5), don't show decimals (would render i.e. ₳5.00 otherwise)
              0
            : assetDecimals;

          return (
            <div className="inline-flex w-fit items-center gap-1">
              {isLP && (
                <Text tag="span" size="xs">
                  ~{" "}
                </Text>
              )}
              <Balance
                formatOpts={{ maximumFractionDigits }}
                locale={currentLocale}
                privacy={privacyEnabled}
                size="xs"
                showSymbol={isAdaAssetId(original.asset.metadata.assetId) || isLP}
                usdValue={original.totalAmountInUSD}
                value={isLP ? original.totalAmountInADA : original.assetAmount}
              />
            </div>
          );
        },
      },
      {
        header: "%",
        accessorKey: "percentageLocked",
        cell: ({ row: { original } }) => {
          if (!original.percentageLocked) return "-";
          return (
            <Percentage
              locale={currentLocale}
              formatOpts={{
                // @ts-ignore
                roundingMode: "floor", // Experimental, but working for modern browsers: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#roundingmode
              }}
              privacy={privacyEnabled}
              size="xs"
              value={original.percentageLocked}
            />
          );
        },
      },
    ],
    [currentLocale, privacyEnabled, t]
  );
  const data = mergedPosition?.value ?? [];

  const tableOptions: TableOptions<IEnhancedValue> = {
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  };

  const isLoadingNode = useMemo(() => {
    if (
      Boolean(pendingPositionTransactions.length) ||
      Boolean(pendingUnlockingPositionTransactions.length)
    ) {
      return (
        <Tooltip
          trigger={
            <Text tag="p" size="xs">
              {t("portfolio.positions.tx.processing")}
            </Text>
          }
          tooltipLabel={
            <PendingTransactions
              context="position"
              transactions={pendingYieldFarmingTransactions}
            />
          }
        />
      );
    }
    if (isLoading) return t("portfolio.positions.isLoading");
    return null;
  }, [isLoading, pendingPositionTransactions.length, pendingUnlockingPositionTransactions.length]);

  return (
    <>
      <div className="grid flex-1">
        <div className="flex items-center gap-2">
          <Text tag="p" weight="semibold">
            {title}
          </Text>

          {pendingPositionTransactions.length || pendingUnlockingPositionTransactions.length ? (
            <Tooltip
              trigger={<ArrowPathIcon className="h-4 w-4 motion-safe:animate-spin" />}
              tooltipLabel={
                <PendingTransactions
                  context="position"
                  transactions={pendingYieldFarmingTransactions}
                />
              }
            />
          ) : null}
        </div>
        {description && (
          <Text size="xs" tag="span" variant="silent">
            {description}
          </Text>
        )}
      </div>

      <div className="grid gap-6">
        <Table
          tableCellClassName="!px-2"
          bodyClassName="text-sm"
          tableOptions={tableOptions}
          emptyStateMessage={
            <div
              className="flex flex-col justify-center"
              data-testid={testIds.yieldFarming.positions.table.noContent}
            >
              <NoDataIndicator context="yieldFarmingPositions" />
              <Button
                onClick={handleOpenDialog}
                size="sm"
                variant="secondary-outlined"
                data-testid={testIds.yieldFarming.positions.ctas.create}
              >
                {t("portfolio.positions.create")}
              </Button>
            </div>
          }
          headersClassName={cx("text-xs", toolkitCx.layout.background.card)}
          isLoading={
            isLoading ||
            Boolean(pendingPositionTransactions.length) ||
            Boolean(pendingUnlockingPositionTransactions.length)
          }
          isLoadingNode={isLoadingNode}
          isSortable
          textAlignment="left"
          withStickyTableHeader
        />

        {!isLoading && Boolean(mergedPosition?.value?.length) && !hideButton ? (
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleOpenUnlockDialog}
              size="sm"
              variant="secondary-outlined"
              data-testid={testIds.yieldFarming.positions.ctas.unlockAll}
            >
              {t("portfolio.positions.unlock")}
            </Button>
            <Button
              onClick={handleOpenDialog}
              size="sm"
              variant="secondary-outlined"
              data-testid={testIds.yieldFarming.positions.ctas.update}
            >
              {t("portfolio.positions.action")}
            </Button>
          </div>
        ) : null}
      </div>

      <YieldFarmingPositionDialogContextProvider mergedPosition={mergedPosition}>
        {/** We need the exchange context provider in order to use the error handler */}
        <ExchangeContextProvider>
          {isDialogOpen && (
            <UpdateYieldFarmingPositionDialog
              closeDialog={handleCloseDialog}
              lpAssetsPoolMap={lpAssetsPoolMap ?? new Map()}
              onOpenChange={setIsDialogOpen}
              onMutationSuccess={handleOnMutationSuccess}
              open={isDialogOpen}
              isLoading={isLoadingYieldFarmingPositions}
            />
          )}

          {isUnlockDialogOpen && (
            <UnlockYieldFarmingPositionsDialog
              closeDialog={handleCloseUnlockDialog}
              onOpenChange={setIsUnlockDialogOpen}
              open={isUnlockDialogOpen}
              onMutationSuccess={handleOnMutationSuccess}
            />
          )}
        </ExchangeContextProvider>
      </YieldFarmingPositionDialogContextProvider>
    </>
  );
};

export default PositionTable;
